import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { AppSettingsProvider } from './context/AppSettingsContext';
import Login from './Pages/Login';
import HomePage from './Pages/HomePage';
import PlaceDetailPage from './Pages/PlaceDetailPage';
import QuestionPage from './Pages/QuestionPage';
import Header from './components/Header';
import GeoDebugPage from './Pages/GeoDebugPage';
import "./App.css";
import RolesPage from './Pages/RolesPage';
import PlacesMap from './Pages/PlacesMap';
import CustomRoutingMap from './Pages/CustomRoutingMap';
import PlacesMapWithRouting from './Pages/PlacesMapWithRouting';
import NavigationMap from './Pages/NavigationMap';
import QrRouteResolver from './Pages/QrRouteResolver';
import GuestThankYouPage from './Pages/GuestThankYouPage';
import TasksPage from './Pages/TasksPage';
import PrivacyPolicyPage from './Pages/PrivacyPolicyPage';
import TermsOfServicePage from './Pages/TermsOfServicePage';
import { Role, Place, PlaceWithDistance } from './types';
import { deleteCurrentPushSubscription, syncPushSubscription } from './notifications/pushNotifications';
import { AppErrorBoundary } from './monitoring/ErrorBoundary';
import { clearMonitoringUser, syncMonitoringContext } from './monitoring/sentry';

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem("device_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("device_id", id);
  }
  return id;
}

function AppRoutes() {
  const [loggedIn, setLoggedIn] = useState<string | null>(localStorage.getItem('userId'));
  const [deviceId] = useState<string>(getOrCreateDeviceId());
  const [placeDetail, setPlaceDetail] = useState<Place | null>(null);
  const [isOneInventory, setIsOneInventory] = useState(false);
  const [userRolesdetail, setUserRolesDetail] = useState<Role[] | undefined>(undefined);
  const [userSelectedRoles, setSelectedRoles] = useState<string[] | undefined>(undefined);
  const [placesForMap, setPlacesForMap] = useState<PlaceWithDistance[]>([]);
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  
  const onLoginSuccess = (userId: string) => {
    setLoggedIn(userId);
    syncMonitoringContext();
    setTimeout(() => {
      void syncPushSubscription();
    }, 0);
  };

  const onLogout = () => {
    void deleteCurrentPushSubscription("logout");
    clearMonitoringUser();
    localStorage.removeItem("userId");
    localStorage.removeItem("userName");
    localStorage.removeItem("userLoginName");
    localStorage.removeItem("telegramId");
    setLoggedIn(null);
  };
  
  const [inventory, setInventory] = useState<string[]>([]);
  const [qrNote, setQrNote] = useState<string | null>(null);
  const navigate = useNavigate();
  const isAuthenticated = Boolean(loggedIn || localStorage.getItem("userId"));
  const currentUserId = localStorage.getItem("userId") || "";
  const protectedRoute = (element: React.ReactElement) =>
    isAuthenticated ? element : null;

  useEffect(() => {
    if (isAuthenticated) {
      void syncPushSubscription();
    }
  }, [isAuthenticated, loggedIn]);

  return (
    <AppSettingsProvider>
      {isAuthenticated && (
        <Header
          placesForMap={placesForMap}
          location={location}
          setLocation={setLocation}
          isOneInventory={isOneInventory}
          placeDetail={placeDetail}
          fetchDetail={() => placeDetail && navigate(`/place/${placeDetail.id}`)}
          setPlaceDetail={setPlaceDetail}
          onLogout={onLogout}
        />
      )}

      <Routes>
        <Route
          path="/"
          element={protectedRoute(
            <HomePage
              key={localStorage.getItem("eventId") || "home"}
              setPlacesForMap={setPlacesForMap}
              userRolesdetail={userRolesdetail}
              setUserRolesDetail={setUserRolesDetail}
              setPlaceDetail={setPlaceDetail}
              location={location}
              setLocation={setLocation}
            />
          )}
        />

        <Route
          path="/roles"
          element={protectedRoute(
            <RolesPage
              placeDetail={placeDetail}
              userRolesdetail={userRolesdetail}
              setSelectedRoles={setSelectedRoles}
            />
          )}
        />
        <Route path="/tasks/:taskId" element={protectedRoute(<TasksPage />)} />
        <Route path="/tasks" element={protectedRoute(<TasksPage />)} />

        <Route path="/geo-debug" element={protectedRoute(<GeoDebugPage />)} />

        <Route
          path="/map"
          element={protectedRoute(
            <PlacesMap
              places={placesForMap}
              setPlacesForMap={setPlacesForMap}
              setUserRolesDetail={setUserRolesDetail}
              setPlaceDetail={setPlaceDetail}
              placeDetail={placeDetail}
              location={location}
              setLocation={setLocation}
            />
          )}
        />

        <Route
          path="/login"
          element={!isAuthenticated ? <Login onLoginSuccess={onLoginSuccess} /> : <Navigate to="/" replace />}
        />
        <Route
          path="/:qrToken"
          element={
            <QrRouteResolver
              setInventory={setInventory}
              setIsOneInventory={setIsOneInventory}
              setPlaceDetail={setPlaceDetail}
              setPlacesForMap={setPlacesForMap}
              setQrNote={setQrNote}
            />
          }
        />
        <Route path="/custom-routing" element={protectedRoute(<CustomRoutingMap />)} />
        <Route
          path="/navigation"
          element={protectedRoute(
            <NavigationMap
              places={placesForMap}
              setPlaceDetail={setPlaceDetail}
              placeDetail={placeDetail}
              location={location}
              setLocation={setLocation}
              user_id={currentUserId}
            />
          )}
        />
        <Route
          path="placewithRouting"
          element={protectedRoute(
            <PlacesMapWithRouting
              places={placesForMap}
              setPlaceDetail={setPlaceDetail}
              placeDetail={placeDetail}
              location={location}
              setLocation={setLocation}
              user_id={currentUserId}
            />
          )}
        />
        <Route
          path="/place/:id"
          element={
            <PlaceDetailPage
              userSelectedRoles={userSelectedRoles}
              inventory={inventory}
              setIsOneInventory={setIsOneInventory}
              setInventory={setInventory}
              setPlaceDetail={setPlaceDetail}
              location={location}
              placesForMap={placesForMap}
            />
          }
        />
        <Route
          path="/questions"
          element={
            <QuestionPage
              inventory={inventory}
              isOneInventory={isOneInventory}
              setInventory={setInventory}
              deviceId={deviceId}
              placeDetail={placeDetail}
              setPlaceDetail={setPlaceDetail}
              userRolesdetail={userRolesdetail}
              qrNote={qrNote}
              setQrNote={setQrNote}
            />
          }
        />
        <Route path="/thank-you" element={<GuestThankYouPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="*" element={isAuthenticated ? <Navigate to="/" replace /> : null} />
      </Routes>
    </AppSettingsProvider>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <Router>
        <AppRoutes />
      </Router>
    </AppErrorBoundary>
  );
}
